import React, { useEffect, useState } from "react";
import {
  Button,
  Confirm,
  Container,
  Dropdown,
  Form,
  FormField,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Segment,
} from "semantic-ui-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { MeetingNotesList } from "../components/sidekick/MeetingNotesList";
import { useDeleteMeetingNotesTemplate } from "../hooks/useDeleteMeetingNotesTemplate";
import { useMeetingNotesPreview } from "../hooks/useMeetingNotesPreview";
import { useMeetingNotesTemplates } from "../hooks/useMeetingNotesTemplates";
import { useSaveMeetingNotesTemplate } from "../hooks/useSaveMeetingNotesTemplate";
import { useSetDefaultMeetingNotesTemplate } from "../hooks/useSetDefaultMeetingNotesTemplate";
import { colors, style } from "../style";
import { getSentimentIcon } from "../utils/getSentimentIcon";

const MAX_LENGTH = 2000;

// Sentinels for the list selection. Real templates are selected by their sqid.
const BUILT_IN = "__built_in__";
const NEW_TEMPLATE = "__new__";
// Pending-navigation target for the Users link, alongside template ids.
const USERS_PAGE = "__users_page__";

const builtInName = (meta) => meta.built_in?.name ?? "Sidekick default";

function UnsavedDot() {
  return (
    <span
      title="Unsaved changes"
      style={{
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        background: colors.amber,
      }}
    />
  );
}

// Example instructions offered as chips. See addExample for how they're added.
const PRESETS = [
  {
    key: "action",
    label: "Action items & owners",
    text: "Focus the meeting notes on action items and who owns them. Skip anything that isn't a task or commitment.",
  },
  {
    key: "decisions",
    label: "Decisions only",
    text: "Only capture decisions that were actually made during the call. Leave out general discussion.",
  },
  {
    key: "risk",
    label: "Risks & objections",
    text: "Pay attention to objections, risks, or blockers the customer raises, and flag anything that needs follow-up urgently.",
  },
  {
    key: "brief",
    label: "Brief paragraph",
    text: "A brief paragraph about the call",
  },
];

const sectionLabelStyle = {
  margin: 0,
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: colors.textLight,
};

// Semantic's Segment is pure black with a bright border in this theme, which
// clashes with the navy wells inside these panels.
const panelStyle = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: "10px",
  boxShadow: "none",
};

const readOnlyBoxStyle = {
  padding: "14px 16px",
  borderRadius: "8px",
  background: colors.pageBackground,
  border: `1px solid ${colors.border}`,
  color: colors.textStrong,
  whiteSpace: "pre-wrap",
};

// Small caps label used for the preview's section headings, so the generated
// notes don't dwarf the editor beside them.
const previewHeadingStyle = {
  margin: "18px 0 6px",
  fontSize: "12px",
  fontWeight: 900,
  letterSpacing: "0.08em",
  color: colors.tealText,
};

function PresetChips({ onSelect, disabled }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
      {PRESETS.map((chip) => (
        <Button
          key={chip.key}
          type="button"
          size="tiny"
          basic
          inverted
          disabled={disabled}
          style={{ borderRadius: "999px", marginBottom: "8px" }}
          onClick={() => onSelect(chip.text)}
        >
          <Icon name="plus" />
          {chip.label}
        </Button>
      ))}
    </div>
  );
}

function SentimentLine({ sentiment }) {
  if (!sentiment) return null;
  const rating = Math.min(
    5,
    Math.max(1, Math.ceil((sentiment.score ?? 0) / 20)),
  );
  const icon = getSentimentIcon(rating);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      {icon && (
        <Icon name={icon.name} size="big" style={{ color: icon.color }} />
      )}
      <span style={{ fontSize: "13px", color: colors.textMuted }}>
        {sentiment.summary}
      </span>
    </div>
  );
}

function PreviewCard({ preview, isStale }) {
  const { data, isLoading, isFetching, isError, refetch } = preview;

  if (isError) {
    return (
      <Message negative>
        <Message.Header>Couldn't generate preview</Message.Header>
        <p>Something went wrong reaching the AI. Please try again.</p>
        <Button type="button" size="tiny" onClick={() => refetch()}>
          Try again
        </Button>
      </Message>
    );
  }

  if (isLoading || !data) {
    return (
      <div style={{ padding: "48px 0" }}>
        <Loader active inline="centered" size="small">
          Generating preview…
        </Loader>
      </div>
    );
  }

  return (
    <div
      style={{
        opacity: isFetching || isStale ? 0.45 : 1,
        transition: "opacity 120ms",
      }}
    >
      <SentimentLine sentiment={data.sentiment} />

      {data.summary?.trim() && (
        <>
          <h3 style={previewHeadingStyle}>SUMMARY</h3>
          <p>{data.summary}</p>
        </>
      )}

      <h3 style={previewHeadingStyle}>MEETING NOTES</h3>
      {data.meeting_notes?.length ? (
        <MeetingNotesList
          sections={data.meeting_notes}
          headerStyle={{ fontSize: "15px", margin: "12px 0 4px" }}
        />
      ) : (
        <p style={{ color: colors.textLight }}>
          No meeting notes were generated for this call with these instructions.
        </p>
      )}
    </div>
  );
}

function DefaultMarker() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "11px",
        fontWeight: 700,
        color: colors.tealText,
      }}
    >
      <Icon name="star" fitted size="small" />
      Default
    </span>
  );
}

function TemplateListItem({
  name,
  subline,
  isDefault,
  isActive,
  isDirty,
  onClick,
}) {
  return (
    <button
      type="button"
      aria-current={isActive ? "true" : undefined}
      onClick={onClick}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        width: "100%",
        padding: "12px",
        textAlign: "left",
        borderRadius: "8px",
        border: `1px solid ${isActive ? colors.tealBorder : "transparent"}`,
        background: isActive ? colors.tealTint : "transparent",
        color: colors.text,
        font: "inherit",
        cursor: "pointer",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontWeight: 700 }}>{name}</span>
        <span
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {isDefault && <DefaultMarker />}
          {isDirty && <UnsavedDot />}
        </span>
      </span>
      {subline && (
        <span style={{ fontSize: "12.5px", color: colors.textMuted }}>
          {subline}
        </span>
      )}
    </button>
  );
}

const assignedSubline = (count) =>
  count
    ? `Assigned to ${count} user${count === 1 ? "" : "s"}`
    : "No one assigned";

function TemplateList({
  templates,
  meta,
  selectedId,
  isDirty,
  onSelect,
  onNew,
}) {
  const isNew = selectedId === NEW_TEMPLATE;
  return (
    <Segment
      as="nav"
      aria-label="Templates"
      style={{
        ...panelStyle,
        // Grows by a negligible share beside the workspace, but fills the row
        // once wrapped above it on narrow screens.
        flex: "1 1 260px",
        margin: 0,
        padding: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <div style={{ ...sectionLabelStyle, padding: "6px 12px 8px" }}>
        Templates
      </div>
      <TemplateListItem
        name={builtInName(meta)}
        subline="Built in"
        isDefault={!meta.customer_default_id}
        isActive={selectedId === BUILT_IN}
        onClick={() => onSelect(BUILT_IN)}
      />
      {templates.map((template) => (
        <TemplateListItem
          key={template.id}
          name={template.name}
          subline={assignedSubline(template.users_count)}
          isDefault={template.is_customer_default}
          isActive={selectedId === template.id}
          isDirty={selectedId === template.id && isDirty}
          onClick={() => onSelect(template.id)}
        />
      ))}
      {isNew && (
        <TemplateListItem
          name="New template"
          subline="Not saved yet"
          isActive
          isDirty={isDirty}
        />
      )}
      {templates.length === 0 && !isNew && (
        <p
          style={{
            margin: "4px 12px 4px",
            fontSize: "12.5px",
            color: colors.textMuted,
          }}
        >
          Make templates for different kinds of calls, like sales or support,
          and give them to the people who make those calls.
        </p>
      )}
      <Button
        type="button"
        fluid
        basic
        inverted
        style={{ marginTop: "8px" }}
        onClick={onNew}
      >
        <Icon name="plus" />
        New template
      </Button>
    </Segment>
  );
}

function DefaultTemplatePicker({
  templates,
  meta,
  isSaving,
  onChange,
  onUsersLinkClick,
}) {
  const options = [
    {
      key: BUILT_IN,
      value: BUILT_IN,
      text: builtInName(meta),
      description: "Built in",
    },
    ...templates.map((t) => ({ key: t.id, value: t.id, text: t.name })),
  ];

  return (
    <Segment
      style={{
        ...panelStyle,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "16px 32px",
        padding: "20px 24px",
        marginTop: "24px",
      }}
    >
      <div style={{ flex: "1 1 360px" }}>
        <Header as="h3" id="default-template-label" style={{ margin: 0 }}>
          Default template
        </Header>
        <p
          style={{
            margin: "4px 0 0",
            fontSize: "13.5px",
            color: colors.textMuted,
          }}
        >
          Used for everyone who hasn't been given a template of their own. You
          can give people their own template in{" "}
          <Link to="/admin/users" onClick={onUsersLinkClick}>
            Users
          </Link>
          .
        </p>
      </div>
      <Dropdown
        selection
        aria-labelledby="default-template-label"
        options={options}
        value={meta.customer_default_id ?? BUILT_IN}
        loading={isSaving}
        disabled={isSaving}
        // Each change saves straight away, so arrowing through the list or
        // tabbing out mustn't count as picking.
        selectOnNavigation={false}
        selectOnBlur={false}
        onChange={(_, { value }) => onChange(value === BUILT_IN ? null : value)}
        style={{ flex: "0 1 340px", minWidth: "240px" }}
      />
    </Segment>
  );
}

function SidekickSettings() {
  const navigate = useNavigate();
  const { templates, meta, isLoading, isError, isFetching, refetch } =
    useMeetingNotesTemplates();

  const [selectedId, setSelectedId] = useState(BUILT_IN);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [editorName, setEditorName] = useState("");
  const [editorInstructions, setEditorInstructions] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // Where the admin was trying to go when they had unsaved changes. Non-null
  // opens the discard confirmation.
  const [pendingSelection, setPendingSelection] = useState(null);

  const builtInInstructions = (meta.built_in?.instructions ?? "").trim();

  // The preview API treats empty instructions as the built-in prompt, so text
  // identical to the built-in maps to "" and shares its cached preview rather
  // than paying for a second AI call that would produce the same notes.
  const toPreviewText = (text) => {
    const trimmed = (text ?? "").trim();
    return builtInInstructions && trimmed === builtInInstructions
      ? ""
      : trimmed;
  };

  // Land on the customer's current default the first time templates load, so
  // admins see the template actually in use rather than always the built-in.
  useEffect(() => {
    if (hasInitialized || isLoading || isError) return;
    setHasInitialized(true);
    if (meta.customer_default_id) {
      const current = templates.find((t) => t.id === meta.customer_default_id);
      if (current) {
        setSelectedId(current.id);
        setEditorName(current.name);
        setEditorInstructions(current.instructions);
        setPreviewText(current.instructions);
        return;
      }
    }
    setPreviewText("");
  }, [hasInitialized, isLoading, isError, meta.customer_default_id, templates]);

  const selectedTemplate = templates.find((t) => t.id === selectedId);
  const isNew = selectedId === NEW_TEMPLATE;
  const isBuiltIn = selectedId === BUILT_IN;

  const isDirty = isNew
    ? Boolean(editorName.trim() || editorInstructions.trim())
    : selectedTemplate
      ? editorName.trim() !== selectedTemplate.name ||
        editorInstructions !== selectedTemplate.instructions
      : false;

  const remaining = MAX_LENGTH - editorInstructions.length;
  const previewIsStale =
    !isBuiltIn &&
    toPreviewText(editorInstructions) !== toPreviewText(previewText);

  const isCurrentDefault = isBuiltIn
    ? !meta.customer_default_id
    : Boolean(selectedTemplate?.is_customer_default);

  const preview = useMeetingNotesPreview(toPreviewText(previewText));

  const {
    saveMeetingNotesTemplateMutate,
    formErrors,
    resetFormErrors,
    isLoading: isSaving,
  } = useSaveMeetingNotesTemplate((saved) => {
    if (!saved?.id) return;
    setSelectedId(saved.id);
    // Re-sync the editor from what was actually stored. handleSave trims the
    // name and the API trims incoming strings, so otherwise a name typed with
    // a trailing space leaves the form looking permanently dirty. previewText
    // is deliberately left alone: nudging it would kick off a fresh AI
    // preview nobody asked for.
    setEditorName(saved.name);
    setEditorInstructions(saved.instructions);
  });

  const { deleteMeetingNotesTemplateMutate, isLoading: isDeleting } =
    useDeleteMeetingNotesTemplate(() => {
      setDeleteConfirmOpen(false);
      // Bypass selectItem's unsaved-changes prompt: the template is already
      // gone server-side, so there's nothing left to discard back to, and
      // asking would leave selectedId pointed at a dead id if cancelled.
      setSelectedId(BUILT_IN);
      setEditorName("");
      setEditorInstructions("");
      setPreviewText("");
    });

  const { setDefaultMeetingNotesTemplateMutate, isLoading: isSettingDefault } =
    useSetDefaultMeetingNotesTemplate();

  const loadIntoEditor = (id) => {
    resetFormErrors();
    setSelectedId(id);
    if (id === BUILT_IN || id === NEW_TEMPLATE) {
      setEditorName("");
      setEditorInstructions("");
      setPreviewText("");
      return;
    }
    const template = templates.find((t) => t.id === id);
    setEditorName(template?.name ?? "");
    setEditorInstructions(template?.instructions ?? "");
    setPreviewText(template?.instructions ?? "");
  };

  const selectItem = (id) => {
    if (id === selectedId) return;
    if (isDirty) {
      setPendingSelection(id);
      return;
    }
    loadIntoEditor(id);
  };

  // Starts an unsaved new template seeded from the built-in prompt. Its
  // preview is the built-in's (already cached), so nothing is regenerated.
  const duplicateBuiltIn = () => {
    resetFormErrors();
    setSelectedId(NEW_TEMPLATE);
    setEditorName(`${builtInName(meta)} (copy)`);
    setEditorInstructions(meta.built_in?.instructions ?? "");
    setPreviewText("");
  };

  const discardChanges = () => {
    if (isNew) {
      loadIntoEditor(meta.customer_default_id ?? BUILT_IN);
      return;
    }
    loadIntoEditor(selectedId);
  };

  // Appends rather than replaces so a click can't wipe what the admin wrote.
  // Only an empty editor previews straight away: preset text on its own is
  // likely cached, anything combined would cost a fresh AI call.
  const addExample = (text) => {
    const current = editorInstructions.trim();
    if (!current) {
      setEditorInstructions(text);
      setPreviewText(text);
      return;
    }
    if (current.includes(text)) return;
    const combined = `${current}\n\n${text}`;
    if (combined.length > MAX_LENGTH) {
      toast.error(
        `That example won't fit within the ${MAX_LENGTH} character limit.`,
      );
      return;
    }
    setEditorInstructions(combined);
  };

  const deleteMessage = () => {
    const count = selectedTemplate?.users_count ?? 0;
    const parts = [];
    if (selectedTemplate?.is_customer_default) {
      parts.push(
        `This is your organisation's default template. Everyone without a template of their own will switch to ${
          builtInName(meta)
        }.`,
      );
    }
    if (count) {
      parts.push(
        `${count} user${count === 1 ? " is" : "s are"} assigned to it and will fall back to the default template.`,
      );
    }
    parts.push("You won't be able to undo this.");
    return parts.join(" ");
  };

  const handleSave = () => {
    if (!editorName.trim() || !editorInstructions.trim()) return;
    saveMeetingNotesTemplateMutate({
      id: isNew ? undefined : selectedId,
      name: editorName.trim(),
      instructions: editorInstructions,
    });
  };

  if (isError) {
    return (
      <Container fluid style={{ padding: "40px 32px 60px" }}>
        <Header as="h1" style={{ marginBottom: "4px" }}>
          Meeting notes templates
        </Header>
        <Message negative style={{ maxWidth: "62ch" }}>
          <Message.Header>Couldn't load templates</Message.Header>
          <p>
            Something went wrong fetching your meeting notes templates, so they
            can't be shown or edited right now. Nothing has been changed.
          </p>
          <Button
            type="button"
            size="tiny"
            loading={isFetching}
            disabled={isFetching}
            onClick={() => refetch()}
          >
            Try again
          </Button>
        </Message>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container fluid style={{ padding: "40px 32px 60px" }}>
        <Header as="h1" style={{ marginBottom: "4px" }}>
          Meeting notes templates
        </Header>
        <Loader active inline="centered" style={{ marginTop: "64px" }}>
          Loading templates…
        </Loader>
      </Container>
    );
  }

  const canSave =
    !isSaving && isDirty && editorName.trim() && editorInstructions.trim();

  return (
    <Container fluid style={{ padding: "40px 32px 60px" }}>
      <Header as="h1" style={{ marginBottom: "4px" }}>
        Meeting notes templates
      </Header>
      <p style={{ color: colors.textMuted, maxWidth: "70ch" }}>
        Templates tell Sidekick what to focus on when it writes up a call.
        Preview any template against a sample call before you save it.
      </p>

      <DefaultTemplatePicker
        templates={templates}
        meta={meta}
        isSaving={isSettingDefault}
        onChange={setDefaultMeetingNotesTemplateMutate}
        onUsersLinkClick={(e) => {
          if (!isDirty) return;
          e.preventDefault();
          setPendingSelection(USERS_PAGE);
        }}
      />

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "24px",
          marginTop: "24px",
          alignItems: "stretch",
        }}
      >
        <TemplateList
          templates={templates}
          meta={meta}
          selectedId={selectedId}
          isDirty={isDirty}
          onSelect={selectItem}
          onNew={() => selectItem(NEW_TEMPLATE)}
        />

        <Segment
          as="main"
          style={{
            ...panelStyle,
            flex: "999 1 640px",
            minWidth: 0,
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* ---------------- Header ---------------- */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "12px",
              padding: "20px 24px",
              borderBottom: `1px solid ${colors.border}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
                marginRight: "auto",
              }}
            >
              {isBuiltIn ? (
                <Header as="h2" style={{ margin: 0 }}>
                  {builtInName(meta)}
                </Header>
              ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <input
                    aria-label="Template name"
                    placeholder="Name this template"
                    value={editorName}
                    maxLength={100}
                    disabled={isSaving}
                    onChange={(e) => setEditorName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && canSave && handleSave()}
                    style={{
                      font: "inherit",
                      fontSize: "22px",
                      fontWeight: 700,
                      color: colors.text,
                      background: "transparent",
                      border: 0,
                      borderBottom: `1px dashed ${colors.textFaint}`,
                      padding: "2px 0",
                      outline: "none",
                      width: `${Math.max(editorName.length, 18) + 2}ch`,
                      maxWidth: "100%",
                    }}
                  />
                  {formErrors?.name && (
                    <span
                      style={{
                        marginTop: "4px",
                        fontSize: "12.5px",
                        color: colors.errorText,
                      }}
                    >
                      {formErrors.name[0]}
                    </span>
                  )}
                </div>
              )}
              {isBuiltIn && (
                <Label basic size="small" style={{ background: "transparent" }}>
                  Built in
                </Label>
              )}
              {isCurrentDefault && (
                <Label
                  size="small"
                  style={{
                    background: colors.greenTint,
                    color: colors.greenText,
                  }}
                >
                  <Icon name="check" />
                  Default for your organisation
                </Label>
              )}
            </div>
            {isBuiltIn ? (
              <Button
                type="button"
                style={style.gradientButton}
                onClick={duplicateBuiltIn}
              >
                <Icon name="copy outline" />
                Duplicate and edit
              </Button>
            ) : (
              // Save sits up here rather than under the editor, where a long
              // preview could push it off screen.
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {isDirty && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      marginRight: "4px",
                      fontSize: "13px",
                      color: colors.amber,
                    }}
                  >
                    <UnsavedDot />
                    Unsaved changes
                  </span>
                )}
                {(isDirty || isNew) && (
                  <>
                    <Button
                      type="button"
                      basic
                      inverted
                      disabled={isSaving}
                      onClick={discardChanges}
                    >
                      {isNew ? "Cancel" : "Discard"}
                    </Button>
                    <Button
                      type="button"
                      style={style.gradientButton}
                      loading={isSaving}
                      disabled={!canSave}
                      onClick={handleSave}
                    >
                      Save template
                    </Button>
                  </>
                )}
                {!isNew && (
                  <Button
                    type="button"
                    basic
                    inverted
                    icon="trash alternate outline"
                    aria-label="Delete template"
                    title="Delete template"
                    disabled={isDeleting || isSaving}
                    onClick={() => setDeleteConfirmOpen(true)}
                  />
                )}
              </div>
            )}
          </div>

          {/* ---------------- Editor + preview ---------------- */}
          <div style={{ display: "flex", flexWrap: "wrap", flex: 1 }}>
            <div
              style={{
                flex: "1 1 340px",
                minWidth: 0,
                padding: "20px 24px",
                borderRight: `1px solid ${colors.border}`,
              }}
            >
              {isBuiltIn ? (
                <>
                  <h3 style={sectionLabelStyle}>Instructions</h3>
                  <div
                    style={{
                      display: "flex",
                      gap: "10px",
                      alignItems: "flex-start",
                      margin: "12px 0",
                      padding: "12px 14px",
                      borderRadius: "8px",
                      background: colors.violetTint,
                      border: `1px solid ${colors.violetBorder}`,
                      fontSize: "13.5px",
                      color: colors.textStrong,
                    }}
                  >
                    <Icon name="info circle" style={{ color: colors.violet }} />
                    <span>
                      This is Sidekick's built-in template, so it can't be
                      changed. Duplicate it to make your own version you can
                      edit.
                    </span>
                  </div>
                  <div style={readOnlyBoxStyle}>
                    {meta.built_in?.instructions}
                  </div>
                </>
              ) : (
                <Form onSubmit={handleSave}>
                  <FormField>
                    <label>Instructions</label>
                    {formErrors?.instructions && (
                      <Label color="red" pointing="below">
                        {formErrors.instructions[0]}
                      </Label>
                    )}
                    <Form.TextArea
                      placeholder="e.g. Focus on action items and next steps. Ignore small talk."
                      value={editorInstructions}
                      maxLength={MAX_LENGTH}
                      onChange={(_, { value }) => setEditorInstructions(value)}
                      disabled={isSaving}
                      rows={8}
                    />
                    <div
                      style={{
                        marginTop: "6px",
                        fontSize: "12px",
                        color:
                          remaining < 100 ? colors.amber : colors.textFaint,
                      }}
                    >
                      {editorInstructions.length} / {MAX_LENGTH}
                    </div>
                  </FormField>

                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "13px",
                      margin: "18px 0 10px",
                    }}
                  >
                    Add an example
                  </div>
                  <PresetChips onSelect={addExample} disabled={isSaving} />
                </Form>
              )}
            </div>

            <div
              style={{ flex: "1 1 340px", minWidth: 0, padding: "20px 24px" }}
            >
              <div
                style={{ display: "flex", alignItems: "baseline", gap: "8px" }}
              >
                <h3 style={sectionLabelStyle}>Preview</h3>
                <span
                  style={{
                    marginLeft: "auto",
                    fontSize: "12px",
                    color: colors.textLight,
                  }}
                >
                  Generated from a fixed sample call
                </span>
              </div>

              {previewIsStale && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "12px",
                    marginTop: "12px",
                    padding: "10px 12px 10px 14px",
                    borderRadius: "8px",
                    background: colors.amberTint,
                    border: `1px solid ${colors.amberBorder}`,
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      color: colors.amber,
                      marginRight: "auto",
                    }}
                  >
                    Instructions changed since this preview
                  </span>
                  <Button
                    type="button"
                    size="tiny"
                    basic
                    inverted
                    disabled={isSaving}
                    onClick={() =>
                      setPreviewText(toPreviewText(editorInstructions))
                    }
                  >
                    <Icon name="refresh" />
                    Refresh preview
                  </Button>
                </div>
              )}

              <div
                style={{
                  marginTop: "12px",
                  padding: "18px",
                  borderRadius: "8px",
                  background: colors.pageBackground,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <PreviewCard preview={preview} isStale={previewIsStale} />
              </div>
            </div>
          </div>

        </Segment>
      </div>

      <Confirm
        open={deleteConfirmOpen}
        header="Delete template?"
        content={deleteMessage()}
        confirmButton={{
          content: "Delete",
          loading: isDeleting,
          disabled: isDeleting,
        }}
        cancelButton={{ content: "Cancel", disabled: isDeleting }}
        onCancel={() => !isDeleting && setDeleteConfirmOpen(false)}
        onConfirm={() =>
          !isDeleting && deleteMeetingNotesTemplateMutate(selectedId)
        }
      />

      <Confirm
        open={pendingSelection !== null}
        header="Discard unsaved changes?"
        content="You've changed this template but haven't saved it. If you carry on, your changes will be lost."
        confirmButton={{ content: "Discard changes", negative: true }}
        cancelButton="Keep editing"
        onCancel={() => setPendingSelection(null)}
        onConfirm={() => {
          if (pendingSelection === USERS_PAGE) {
            navigate("/admin/users");
          } else {
            loadIntoEditor(pendingSelection);
          }
          setPendingSelection(null);
        }}
      />
    </Container>
  );
}

export default SidekickSettings;
